import type { paths } from "@lightmill/log-api"
import "./html-loader-element"
import createClient from "openapi-fetch"
import commonStyleUrl from "../common.css?url"
import { getElement } from "../lib/get-elements"
import { type ObservedAttribute, html } from "../lib/utils"
import {
	WithAttributeProps,
	boolean,
	string,
} from "../mixins/with-attributes-props"

const template = html`
	<style>
		@import url(${commonStyleUrl});
		.main {
			display: flex;
			gap: var(--space-m);
			align-items: center;
			justify-content: center;
		}

    x-loader {
      display: none;
    }
		.main[data-status="loading"] .main-loader {
			display: block;
		}
		.username-wrapper,
		.error-wrapper,
		.no-username-wrapper {
			display: flex;
			align-items: center;
			gap: var(--space-s);
		}
		.no-username-wrapper {
			font-style: italic;
			color: var(--gray-400);
		}
		.error-wrapper {
			color: var(--red-300);
		}
		.main:not([data-status="error"]) .error-wrapper {
			display: none
		}
		.main:not([data-status="ready"]) {
			.username-wrapper,
			.no-username-wrapper,
			.logged-in,
			.logged-out {
				display: none;
			}
		}
		.main:not([data-username]) {
			.username-wrapper {
				display: none;
			}
			.logged-in {
				display: none;
			}
		}
		.main[data-username] {
			.no-username-wrapper {
				display: none;
			}
			.logged-out {
				display: none;
			}
		}
    :host([no-controls]) .controls {
				display: none;
		}

	</style>
  <div class="main" data-status="idle">
    <x-loader class="main-loader"></x-loader>
    <div class="content">
    	<span class="username-wrapper">
				<iconify-icon icon="tabler:user-filled" inline></iconify-icon>
				<span class="username"></span>
			</span>
      <span class="no-username-wrapper">
				<iconify-icon icon="tabler:ghost-3-filled" inline></iconify-icon>
				<span class="no-username">unknown user</span>
			</span>
      <span class="error-wrapper">
				<iconify-icon icon="tabler:alert-circle-filled" inline></iconify-icon>
				<span class="error">Error</span>
			</span>
    </div>
    <slot name="logged-in" class="logged-in"></slot>
    <slot name="logged-out" class="logged-out"></slot>
  </div>
`

const statusValues = ["idle", "loading", "error", "ready"] as const
export type Status = (typeof statusValues)[number]

export class HTMLUsernameElement extends WithAttributeProps(HTMLElement, {
	noControls: boolean(),
	apiSrc: string(),
}) {
	#mainElement: HTMLElement
	#usernameElement: HTMLElement

	constructor() {
		super()
		let shadowRoot = this.attachShadow({ mode: "closed" })
		shadowRoot.appendChild(template.content.cloneNode(true))
		this.#mainElement = getElement(".main", shadowRoot, HTMLElement)
		this.#usernameElement = getElement(
			".username",
			this.#mainElement,
			HTMLElement,
		)
	}

	get status() {
		if (statusValues.includes(this.#mainElement.dataset.status as Status)) {
			throw new Error(`Invalid status: ${this.#mainElement.dataset.status}`)
		}
		return this.#mainElement.dataset.status as Status
	}

	#setStatus(
		status: "error",
		options: { noDispatchEvent?: boolean; error: Error },
	): void
	#setStatus(
		status: Exclude<Status, "error">,
		options?: { noDispatchEvent?: boolean; error?: null },
	): void
	#setStatus(
		status: Status,
		{
			noDispatchEvent,
			error,
		}: { noDispatchEvent?: boolean; error?: Error | null } = {
			noDispatchEvent: false,
		},
	): void {
		const info = {
			status,
			username: this.username,
			error: error ?? null,
		}
		if (!isLoadEventDetail(info)) {
			throw new Error(`Incoherent status info: ${JSON.stringify(info)}`)
		}
		this.#mainElement.dataset.status = status
		if (noDispatchEvent) {
			return
		}
		this.dispatchEvent(new StatusChangeEvent(info))
	}

	get username() {
		return this.#mainElement.dataset.username ?? null
	}
	#setUsername(username: string | null) {
		if (username == null) {
			delete this.#mainElement.dataset.username
			this.#usernameElement.textContent = ""
		} else {
			this.#mainElement.dataset.username = username
			this.#usernameElement.textContent = username
		}
	}

	#disableAll() {
		for (let elt of this.#mainElement.querySelectorAll("button, input")) {
			elt.setAttribute("disabled", "true")
		}
	}

	#enableAll() {
		for (let elt of this.#mainElement.querySelectorAll("button, input")) {
			elt.removeAttribute("disabled")
		}
	}

	connectedCallback() {
		this.#update()
	}

	disconnectedCallback() {
		this.#abortController?.abort()
		this.#abortController = null
	}

	static observedAttributes = ["api-src"] as const

	attributeChangedCallback() {
		this.#update()
	}

	set(value: { username: string | null }) {
		this.#setUsername(value.username ?? null)
		this.#setStatus("ready")
	}

	update() {
		this.#update()
	}

	#abortController: AbortController | null = null
	#update() {
		if (this.#abortController) {
			this.#abortController.abort()
			this.#abortController = null
		}
		this.#setUsername(null)
		if (!this.apiSrc) {
			this.#setStatus("ready")
			return
		}
		this.#setStatus("loading")
		let client = createClient<paths>({
			baseUrl: this.apiSrc,
		})
		this.#abortController = new AbortController()
		client
			.GET("/sessions/{id}", {
				params: { path: { id: "current" } },
				signal: this.#abortController.signal,
			})
			.then((response) => {
				if (response.response.status === 404) {
					this.#setUsername(null)
					this.#setStatus("ready")
					return
				}
				if (response.error != null) {
					let error = response.error.errors[0]
					throw new Error(error.detail ?? error.code)
				}
				this.#setUsername(response.data.data.attributes.role)
				this.#setStatus("ready")
			})
			.catch((error: unknown) => {
				if (error instanceof Error && error.name === "AbortError") {
					return
				}
				this.#setUsername(null)
				let err = error instanceof Error ? error : new Error(String(error))
				this.#setStatus("error", { error: err })
			})
	}
}

type LoadEventDetail =
	| { status: Exclude<Status, "ready" | "error">; username: null; error: null }
	| { status: "ready"; username: string | null; error: null }
	| { status: "error"; username: null; error: Error }

function isLoadEventDetail(detail: unknown): detail is LoadEventDetail {
	if (typeof detail !== "object" || detail === null) return false
	if (
		!(
			"status" in detail &&
			typeof detail.status === "string" &&
			"username" in detail &&
			"error" in detail
		)
	) {
		return false
	}
	switch (detail.status) {
		case "ready":
			return (
				(detail.username === null || typeof detail.username === "string") &&
				detail.error === null
			)
		case "error":
			return detail.username === null && detail.error instanceof Error
		default:
			return (
				statusValues.includes(detail.status as Status) &&
				detail.username === null &&
				detail.error === null
			)
	}
}

export class StatusChangeEvent extends CustomEvent<LoadEventDetail> {
	static get eventName() {
		return "change" as const
	}
	constructor(detail: LoadEventDetail) {
		super(StatusChangeEvent.eventName, { detail })
	}
}

customElements.define("x-username", HTMLUsernameElement)
